import React, { FunctionComponent } from 'react';

import cn from 'classnames';
import { useSelector } from 'react-redux';

import Spin from '../../../../../components/spin';
import { i18n } from '../../../../../helpers/i18n';
import { countMetrika } from '../../../../../helpers/metrika';
import { useActions } from '../../../../../hooks';
import {
    getVideoFacingMode,
    getVideoInputDeviceSelector,
    selectVirtualBackgroundType,
    setVirtualBackgroundType as setVirtualBackgroundTypeAction,
    VirtualBackgroundType,
} from '../../../../settings';
import { isMobileUa } from '../../../../uatraits';
import { useCurrentVideoStream } from '../../../hooks/useCurrentVideoStream';
import BackgroundOptions, { BackgroundOptionItem } from '../../background-options';
import css from './styles.css';

const backgroundOptionItems: BackgroundOptionItem[] = [
    {
        title: () => i18n('settings', 'camera-background-none'),
        type: VirtualBackgroundType.NONE,
    },
    {
        title: () => i18n('settings', 'camera-background-blurred'),
        type: VirtualBackgroundType.BLURRED,
        isNda: true,
    },
];

const VirtualBackgroundSection: FunctionComponent = () => {
    const { setVirtualBackgroundType } = useActions({ setVirtualBackgroundType: setVirtualBackgroundTypeAction });

    const isMobile = useSelector(isMobileUa);
    const currentDeviceId = useSelector((state) => getVideoInputDeviceSelector(state)?.deviceId);
    const facingMode = useSelector(getVideoFacingMode);
    const virtualBackgroundType = useSelector(selectVirtualBackgroundType);

    const deviceIdOrFacingMode = isMobile ? facingMode : currentDeviceId;

    const { videoRef, isVideoReady, videoInputError, withMirror } = useCurrentVideoStream({
        deviceIdOrFacingMode,
        virtualBackgroundType,
    });

    const setSelectedItemType = (type: VirtualBackgroundType) => {
        setVirtualBackgroundType(type);
        countMetrika('interface elements', 'settings popup', 'virtual background', type);
    };

    const isSpinVisible = !isVideoReady && !videoInputError;

    return (
        <div className={css.container}>
            <div className={css.videoWrapper}>
                {isSpinVisible && (
                    <div className={css.spinOverlay}>
                        <Spin className={css.spin} size="xl" progress />
                    </div>
                )}
                <video
                    className={cn(css.cameraVideo, {
                        [css.mirror]: withMirror,
                    })}
                    ref={videoRef}
                    autoPlay
                    playsInline
                    // remove YaBro custom buttons
                    // eslint-disable-next-line react/no-unknown-property
                    x-yandex-pip="false"
                />
            </div>
            <BackgroundOptions
                items={backgroundOptionItems}
                selectedItemType={virtualBackgroundType}
                setSelectedItemType={setSelectedItemType}
            />
        </div>
    );
};

export default VirtualBackgroundSection;
